import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Polygon3 } from '../Icon';
import styles from './index.module.scss';

interface VideoProps {
  src: string;
  poster: string;
  className?: string;
}

const Video = ({ src, poster, className = '' }: VideoProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showPlayButton, setShowPlayButton] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  // Manual play (click on overlay)
  const onPlay = () => {
    const v = videoRef.current;
    if (!v) return;
    v.controls = true; // ✅ ensure controls on manual start
    const p = v.play();
    setShowPlayButton(false);
    p?.catch(() => {}); // ignore DOMException if any
  };

  // Base media listeners
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const handleCanPlay = () => setIsLoading(false);
    const handlePause = () => setShowPlayButton(true);
    const handleEnded = () => {
      setShowPlayButton(true);
      v.controls = false;
    };
    const handlePlay = () => {
      setShowPlayButton(false);
      if (!v.controls) v.controls = true; // ✅ ensure controls on auto start too
    };

    v.controls = false;

    v.addEventListener('canplay', handleCanPlay);
    v.addEventListener('pause', handlePause);
    v.addEventListener('ended', handleEnded);
    v.addEventListener('play', handlePlay);

    return () => {
      v.removeEventListener('canplay', handleCanPlay);
      v.removeEventListener('pause', handlePause);
      v.removeEventListener('ended', handleEnded);
      v.removeEventListener('play', handlePlay);
    };
  }, []);

  // Auto play/pause on viewport
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    let playPromise: Promise<void> | null = null;
    let playingFlag = false;

    const tryPlay = () => {
      if (!v.paused || playingFlag) return;
      if (document.visibilityState !== 'visible') return;
      playingFlag = true;
      v.controls = true; // ✅ show controls on first auto start
      playPromise = v.play();
      playPromise
        ?.catch(() => {}) // ignore autoplay errors
        .finally(() => (playingFlag = false));
    };

    const tryPause = () => {
      if (playPromise) {
        // wait for play to settle, then pause
        playPromise.finally(() => v.pause());
        playPromise = null;
      } else if (!v.paused) {
        v.pause();
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (e.intersectionRatio > 0.6) {
          tryPlay();
        } else {
          tryPause();
        }
      },
      {
        threshold: [0, 0.25, 0.5, 0.6, 0.75, 1],
        rootMargin: '0px 0px -10% 0px',
      }
    );

    const onVisibility = () =>
      document.visibilityState === 'visible' ? tryPlay() : tryPause();

    observer.observe(v);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return (
    <div className={clsx(styles.videoWrapper, className)}>
      <video
        ref={videoRef}
        width="100%"
        poster={poster}
        className={styles.video}
        muted
        playsInline
        preload="metadata"
      >
        <source src={src} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {isLoading ? (
        <div className={styles.spinnerWrapper}>
          {/* your spinner */}
          <svg
            width="104"
            height="105"
            viewBox="0 0 104 105"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M75.5498 55.0199L39.6216 75.7631L39.6216 34.2767L75.5498 55.0199Z"
              fill="#D9D9D9"
            />
            <rect
              x="0.5"
              y="0.712891"
              width="103"
              height="103"
              rx="51.5"
              stroke="#D7D5D5"
            />
          </svg>
        </div>
      ) : (
        showPlayButton && (
          <button className={styles.playBtn} onClick={onPlay}>
            <Polygon3 className={styles.icon} />
          </button>
        )
      )}
    </div>
  );
};

export default Video;
